﻿import type { AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import axios, { AxiosInstance } from 'axios';
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'

// Global retry interceptor - applies to all axios instances
// Handles network errors and 5xx errors with exponential backoff (like Polly in C#)
// 10 retries covers Azure Function cold-starts and transient failures
function addRetryInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    response => response,
    async error => {
      const config = error.config as any;
      
      // Initialize retry count
      if (!config.retryCount) {
        config.retryCount = 0;
      }
      
      // Only retry on network errors or 5xx errors, NOT on 4xx
      const isRetryableError = 
        !error.response || // Network error (no response)
        error.code === 'ECONNABORTED' || // Timeout
        error.code === 'ENOTFOUND' || // DNS
        error.code === 'ECONNREFUSED' || // Connection refused
        (error.response?.status >= 500); // Server error
      
      const maxRetries = 10;
      const shouldRetry = isRetryableError && config.retryCount < maxRetries;
      
      if (shouldRetry) {
        config.retryCount += 1;
        if (config.headers && config.headers['Idempotency-Key']) {
          config.headers['X-Create-Attempt'] = String(config.retryCount + 1);
        }
        // Exponential backoff with 60s cap: 1s → 2s → 4s → 8s → 16s → 32s → 60s → 60s...
        const delayMs = Math.min(Math.pow(2, config.retryCount) * 1000, 60000);
        
        console.warn(
          `⚠️ Retrying ${config.method?.toUpperCase()} ${config.url} (attempt ${config.retryCount}/${maxRetries}), waiting ${delayMs}ms...`,
          error.code || error.response?.status
        );
        
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return instance(config);
      }
      
      // Failed after all retries
      if (isRetryableError) {
        console.error(`❌ Request failed after ${maxRetries} retries: ${config.method?.toUpperCase()} ${config.url}`, error.code || error.message);
      }
      
      return Promise.reject(error);
    }
  );
}

// Authenticated axios instance (for protected endpoints)
export const eventsAuthenticatedAxios = axios.create({
    baseURL: import.meta.env.VITE_CONTENT_API,
    timeout: 30000 // 30 seconds
})

eventsAuthenticatedAxios.interceptors.request.use(
    async function (request: AxiosRequestConfig) {
        try {
            const authStore = useAuthenticationStore()

            // Wrap getToken in try/catch to prevent MSAL redirect from interrupting requests
            let token: string | null = null
            try {
                token = await authStore.getToken()
            } catch (tokenError) {
                console.warn('[axios] Failed to get token, continuing without auth')
            }

            if (token) {
                ;(request.headers as AxiosRequestHeaders).Authorization = `Bearer ${token}`
            }

            return request
        } catch (error) {
            console.error('[axios-request] Unexpected error:', error)
            return request
        }
    }
)

// Add retry to authenticated axios
addRetryInterceptor(eventsAuthenticatedAxios)

// Default axios instance (for public endpoints and internal use)
const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_CONTENT_API,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add retry to default instance
addRetryInterceptor(axiosInstance)

// Dedicated search axios instance (longer timeout for search operations)
// Uses same baseURL and baseURL as other authenticated instances for consistency
export const searchAxios = axios.create({
  baseURL: import.meta.env.VITE_CONTENT_API,
  timeout: 30000, // 30 seconds for search operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add retry to search instance
addRetryInterceptor(searchAxios)

export default axiosInstance;